"use client";

import { useEffect, type RefObject } from "react";
import { createCardGate, runCursorStep } from "@/lib/cursor-sequence";

export interface CursorPoint {
  x: number;
  y: number;
}

export interface CursorPath {
  points: [CursorPoint, CursorPoint, CursorPoint];
  controls: [
    [CursorPoint, CursorPoint],
    [CursorPoint, CursorPoint],
    [CursorPoint, CursorPoint],
  ];
}

export interface CursorActor {
  markerRef: RefObject<HTMLDivElement>;
  path: CursorPath;
  initialDelay: number;
  moveDuration: number;
  cardDuration: number;
  cardGap: number;
  restDuration: number;
}

const MOVE_SAMPLES = 16;
const CARD_OPEN_DURATION = 240;
const CARD_CLOSE_DURATION = 180;

function wait(duration: number, signal: AbortSignal) {
  signal.throwIfAborted();

  return new Promise<void>((resolve, reject) => {
    const finish = () => {
      signal.removeEventListener("abort", abort);
      resolve();
    };
    const abort = () => {
      window.clearTimeout(timeout);
      reject(signal.reason);
    };
    const timeout = window.setTimeout(finish, duration);
    signal.addEventListener("abort", abort, { once: true });
  });
}

function cubicPoint(
  start: CursorPoint,
  controlA: CursorPoint,
  controlB: CursorPoint,
  end: CursorPoint,
  progress: number,
) {
  const inverse = 1 - progress;

  return {
    x:
      start.x * inverse ** 3 +
      controlA.x * 3 * inverse ** 2 * progress +
      controlB.x * 3 * inverse * progress ** 2 +
      end.x * progress ** 3,
    y:
      start.y * inverse ** 3 +
      controlA.y * 3 * inverse ** 2 * progress +
      controlB.y * 3 * inverse * progress ** 2 +
      end.y * progress ** 3,
  };
}

function buildMoveKeyframes(
  start: CursorPoint,
  controls: [CursorPoint, CursorPoint],
  end: CursorPoint,
): Keyframe[] {
  return Array.from({ length: MOVE_SAMPLES + 1 }, (_, index) => {
    const progress = index / MOVE_SAMPLES;
    const eased = progress ** 2 * (3 - 2 * progress);
    const point = cubicPoint(start, ...controls, end, eased);

    return { left: `${point.x}%`, offset: progress, top: `${point.y}%` };
  });
}

async function animate(
  element: HTMLElement,
  keyframes: Keyframe[],
  duration: number,
  signal: AbortSignal,
) {
  signal.throwIfAborted();
  const animation = element.animate(keyframes, {
    duration,
    easing: "linear",
    fill: "forwards",
  });
  const cancel = () => animation.cancel();
  signal.addEventListener("abort", cancel, { once: true });

  try {
    await animation.finished;
  } finally {
    animation.cancel();
    signal.removeEventListener("abort", cancel);
  }
}

async function openCard(card: HTMLElement, signal: AbortSignal) {
  signal.throwIfAborted();
  card.style.visibility = "visible";
  await animate(
    card,
    [
      { opacity: 0, transform: "translateY(0.2rem) scale(0.72)" },
      { opacity: 1, transform: "translateY(0) scale(1.06)", offset: 0.65 },
      { opacity: 1, transform: "translateY(0) scale(1)" },
    ],
    CARD_OPEN_DURATION,
    signal,
  );
  card.style.opacity = "1";
  card.style.transform = "translateY(0) scale(1)";
}

async function closeCard(card: HTMLElement, signal: AbortSignal) {
  signal.throwIfAborted();
  await animate(
    card,
    [
      { opacity: 1, transform: "translateY(0) scale(1)" },
      { opacity: 0, transform: "translateY(0.2rem) scale(0.72)" },
    ],
    CARD_CLOSE_DURATION,
    signal,
  );
  card.style.opacity = "0";
  card.style.transform = "translateY(0.2rem) scale(0.72)";
  card.style.visibility = "hidden";
}

function resetMarker(marker: HTMLElement, path: CursorPath) {
  marker
    .getAnimations({ subtree: true })
    .forEach((animation) => animation.cancel());
  marker.style.left = `${path.points[0].x}%`;
  marker.style.top = `${path.points[0].y}%`;
  marker.style.zIndex = "2";
  marker.querySelectorAll<HTMLElement>("[data-thought]").forEach((card) => {
    card.style.opacity = "0";
    card.style.transform = "translateY(0.2rem) scale(0.72)";
    card.style.visibility = "hidden";
  });
}

async function runActor(
  actor: CursorActor,
  cardGate: ReturnType<typeof createCardGate>,
  signal: AbortSignal,
) {
  const marker = actor.markerRef.current;
  if (!marker) return;

  await wait(actor.initialDelay, signal);
  let pointIndex = 0;

  while (!signal.aborted) {
    const nextPointIndex = (pointIndex + 1) % actor.path.points.length;
    const card = marker.querySelector<HTMLElement>(
      `[data-thought="${nextPointIndex + 1}"]`,
    );
    if (!card) throw new Error("Cursor thought card is missing");

    await runCursorStep(
      {
        move: async () => {
          marker.style.zIndex = "2";
          await animate(
            marker,
            buildMoveKeyframes(
              actor.path.points[pointIndex],
              actor.path.controls[pointIndex],
              actor.path.points[nextPointIndex],
            ),
            actor.moveDuration,
            signal,
          );
          marker.style.left = `${actor.path.points[nextPointIndex].x}%`;
          marker.style.top = `${actor.path.points[nextPointIndex].y}%`;
          pointIndex = nextPointIndex;
        },
        openCard: async () => {
          marker.style.zIndex = "10";
          await openCard(card, signal);
        },
        holdCard: () => wait(actor.cardDuration, signal),
        closeCard: () => closeCard(card, signal),
        cardGap: () => wait(actor.cardGap, signal),
        rest: async () => {
          marker.style.zIndex = "2";
          await wait(actor.restDuration, signal);
        },
      },
      cardGate,
    );
  }
}

export function useCursorPaths(actors: readonly CursorActor[]) {
  useEffect(() => {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let controller: AbortController | null = null;

    function updateAnimation() {
      controller?.abort();
      actors.forEach((actor) => {
        if (actor.markerRef.current) resetMarker(actor.markerRef.current, actor.path);
      });
      if (reducedMotion.matches) return;

      const nextController = new AbortController();
      controller = nextController;
      const cardGate = createCardGate();
      actors.forEach((actor) => {
        void runActor(actor, cardGate, nextController.signal).catch((error) => {
          if (!nextController.signal.aborted) throw error;
        });
      });
    }

    updateAnimation();
    reducedMotion.addEventListener("change", updateAnimation);

    return () => {
      controller?.abort();
      reducedMotion.removeEventListener("change", updateAnimation);
    };
  }, [actors]);
}
